import React, { useCallback, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { formatDate } from "src/utils/string/stringUtil";
import { showError, showSuccess, showWarning } from "src/utils/toast/toastUtil";

import {
	deleteTQC2090_R01,
	downloadExcel_TQC2090_R01,
	downloadReportPdf_TQC2090_R01,
	getTQC2090_R01List,
	saveTQC2090_R01,
} from "./Api_TQC2090_R01";
import {
	defaultTQC2090_R01Dto,
	TQC2090_R01_COMP_CD,
	TQC2090_R01Dto,
	TQC2090_R01_INSPECT_TYPE,
} from "./Dto_TQC2090_R01";
import { TQC2090_R01Filter, useTQC2090_R01Store } from "./Store_TQC2090_R01";
import {
	createMonthlyRows,
	getInspectionItemsByLine,
	mergeNickelRows,
	NICKEL_LINE,
	splitNickelRow,
} from "./utils/inspectionRowUtils";
import { createTQC2090_R01Params } from "./utils/searchParamUtils";

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC2090_R01Dto> | null>;
}

export const Content_TQC2090_R01 = ({ gridRef }: Props) => {
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	const {
		TQC2090_R01Filter,
		TQC2090_R01Info,
		setTQC2090_R01Filter,
		setTQC2090_R01PastFilter,
		TQC2090_R01List,
		setTQC2090_R01List,
		setOriginalTQC2090_R01List,
		selectedTQC2090_R01List,
		setSelectedTQC2090_R01List,
		setAllCount,
		setScrollTop,
		setColumnState,
		initData,
	} = useTQC2090_R01Store();

	const [clickable, setClickable] = useState(true);

	const defaultTQC2090_R01Row = useCallback(() => ({ ...defaultTQC2090_R01Dto }), []);

	/** 검사기준으로 월 전체 행을 만들고 저장 결과를 병합해 그리드 행으로 정규화한다. */
	const toGridRows = useCallback(
		(
			storedRows: TQC2090_R01Dto[],
			items: TQC2090_R01Dto[],
			filter: TQC2090_R01Filter,
		): TQC2090_R01Dto[] => {
			if (!filter.inspectYm) return [];
			const monthlyRows = createMonthlyRows(
				filter.inspectYm,
				items,
				storedRows,
			);
			const rows = filter.workplaceCd === NICKEL_LINE
				? mergeNickelRows(monthlyRows)
				: monthlyRows;
			return rows.map((item, idx) => ({
				...defaultTQC2090_R01Dto,
				...item,
				valueIndex: idx + 1,
				inserted: false,
				isModified: false,
			}));
		},
		[],
	);

	/** 지정한 필터로 조회한다. 탭 전환처럼 상태 반영 전에 조회해야 할 때 필터를 직접 넘긴다. */
	const fetchWith = useCallback(
		async (filter: TQC2090_R01Filter, notify: boolean) => {
			try {
				setLoadingSpinner(true);
				setScrollTop(null);

				const params = createTQC2090_R01Params(filter);
				const response = await getTQC2090_R01List("search", params);
				const storedRows = response.data.data ?? [];
				const items = getInspectionItemsByLine(filter.workplaceCd);
				const data = toGridRows(
					storedRows,
					items,
					filter,
				);

				setTQC2090_R01List(data);
				setOriginalTQC2090_R01List(data);
				setSelectedTQC2090_R01List([]);

				setAllCount(storedRows.length);
				setTQC2090_R01PastFilter(filter);

				if (notify) {
					data.length > 0
						? showSuccess(`조회 완료 (${data.length}건)`)
						: showWarning("검사기준(재공품/초중종검사)이 등록되지 않았습니다.");
				}
			} catch {
				showError("조회 중 오류가 발생했습니다.");
			} finally {
				setLoadingSpinner(false);
			}
		},
		[
			toGridRows, setLoadingSpinner, setScrollTop, setTQC2090_R01List,
			setOriginalTQC2090_R01List, setSelectedTQC2090_R01List,
			setAllCount, setTQC2090_R01PastFilter,
		],
	);

	// ── 조회 ─────────────────────────────────────────────────
	const fetchData = useCallback(
		() => fetchWith(TQC2090_R01Filter, true),
		[fetchWith, TQC2090_R01Filter],
	);

	/** 저장·삭제 후 현재 조건으로 다시 읽어 목록/원본을 최신화한다. */
	const refetch = useCallback(
		() => fetchWith(TQC2090_R01Filter, false),
		[fetchWith, TQC2090_R01Filter],
	);

	// ── 공정라인 탭 전환 ─────────────────────────────────────
	/** 탭을 바꾸면 해당 라인으로 즉시 재조회한다. */
	const handleLineChange = useCallback(
		(workplaceCd: string) => {
			if (workplaceCd === TQC2090_R01Filter.workplaceCd) return;
			const nextFilter = { ...TQC2090_R01Filter, workplaceCd };
			setColumnState(null);
			setTQC2090_R01Filter({ workplaceCd });
			fetchWith(nextFilter, false);
		},
		[TQC2090_R01Filter, setColumnState, setTQC2090_R01Filter, fetchWith],
	);

	// ── 저장 (수정된 행만) ───────────────────────────────────
	/**
	 * isModified 인 행만 추려 저장한다.
	 * InfoBar 의 검사자/승인자는 전송할 모든 행에 덮어쓴다. (설계 문서 §5)
	 */
	const handleSaveClick = useCallback(async () => {
		const modifiedRows = TQC2090_R01List.filter((row) => row.isModified);

		if (modifiedRows.length === 0) {
			showWarning("변경된 데이터가 없습니다.");
			return;
		}
		if (!TQC2090_R01Info.inspectorEmp) {
			showWarning("검사자를 입력해주세요.");
			return;
		}
		const normalizedRows = TQC2090_R01Filter.workplaceCd === NICKEL_LINE
			? modifiedRows.flatMap(splitNickelRow)
			: modifiedRows;
		const saveRows = normalizedRows.map((row) => ({
			...row,
			inspectDt: formatDate(row.inspectDt),
			compCd: TQC2090_R01_COMP_CD,
			workplaceCd: TQC2090_R01Filter.workplaceCd,
			inspectType: TQC2090_R01_INSPECT_TYPE,
			inspectorEmp: TQC2090_R01Info.inspectorEmp,
			inspectorEmpNm: TQC2090_R01Info.inspectorEmpNm,
			approvorEmpno: TQC2090_R01Info.approvorEmpno,
			approvorEmpNm: TQC2090_R01Info.approvorEmpNm,
		}));

		try {
			setLoadingSpinner(true);
			await saveTQC2090_R01(saveRows);
			await refetch();
			showSuccess("저장이 완료되었습니다.");
		} catch {
			showError("저장 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2090_R01Info, TQC2090_R01Filter, TQC2090_R01List, refetch, setLoadingSpinner]);

	// ── 삭제 (검사결과만) ────────────────────────────────────
	/** 선택한 행의 검사결과를 삭제한다. 저장된 적 없는 행은 대상이 아니다. */
	const handleDeleteClick = useCallback(async () => {
		if (selectedTQC2090_R01List.length === 0) {
			showWarning("삭제할 행을 선택해주세요.");
			return;
		}
		const selected = new Set(selectedTQC2090_R01List);
		const selectedRows = TQC2090_R01List
			.filter((row) => selected.has(row.valueIndex.toString()) && row.inspectNo)
		const normalizedRows = TQC2090_R01Filter.workplaceCd === NICKEL_LINE
			? selectedRows.flatMap(splitNickelRow)
			: selectedRows;
		const targets = normalizedRows.map((row) => ({
				...row,
				inspectDt: formatDate(row.inspectDt),
				compCd: TQC2090_R01_COMP_CD,
				workplaceCd: TQC2090_R01Filter.workplaceCd,
			}));

		if (targets.length === 0) {
			showWarning("삭제할 검사결과가 없습니다.");
			return;
		}

		try {
			setLoadingSpinner(true);
			await deleteTQC2090_R01(targets);
			await refetch();
			showSuccess("삭제가 완료되었습니다.");
		} catch {
			showError("삭제 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		selectedTQC2090_R01List, TQC2090_R01List, TQC2090_R01Filter,
		refetch, setLoadingSpinner,
	]);

	// ── 엑셀 ─────────────────────────────────────────────────
	const handleExcelClick = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			const response = await downloadExcel_TQC2090_R01(
				createTQC2090_R01Params(TQC2090_R01Filter),
			);
			if (!response.data.data) {
				showWarning("엑셀로 내보낼 데이터가 없습니다.");
				return;
			}
			downloadSettingExcel({
				responseData: response.data.data,
				fileName: "초중종검사결과등록.xlsx",
			});
		} catch {
			showError("엑셀 다운로드 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2090_R01Filter, setLoadingSpinner]);

	// ── 출력 (검사일지 PDF) ──────────────────────────────────
	/**
	 * 현재 조회조건으로 검사일지 PDF(Blob)를 받아 새 탭으로 연다.
	 * 양식은 탭(공정라인)에 따라 서버가 고른다. (설계 문서 §8)
	 * 열람 후 5초 뒤 Object URL 을 해제해 메모리 누수를 막는다.
	 */
	const handlePrintClick = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			const response = await downloadReportPdf_TQC2090_R01(
				createTQC2090_R01Params(TQC2090_R01Filter),
			);
			const blob = new Blob([response.data], { type: "application/pdf" });
			const fileUrl = URL.createObjectURL(blob);
			window.open(fileUrl, "_blank");
			setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
		} catch (error) {
			// 응답을 blob 으로 받으므로 서버 오류 본문도 blob 으로 온다.
			// 텍스트로 풀어 원인을 그대로 보여준다. (서버가 text/plain 으로 내려줌)
			const data = (error as { response?: { data?: unknown } })?.response?.data;
			const detail = data instanceof Blob ? (await data.text()).trim() : "";
			showError(detail || "출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2090_R01Filter, setLoadingSpinner]);

	// ── 초기화 ───────────────────────────────────────────────
	const initContainerData = useCallback(() => {
		initData();
		gridRef.current?.api?.deselectAll();
		gridRef.current?.api?.setGridOption("rowData", []);
	}, [gridRef, initData]);

	return {
		clickable, setClickable,
		defaultTQC2090_R01Row,
		fetchData,
		handleLineChange,
		handleSaveClick,
		handleDeleteClick,
		handleExcelClick,
		handlePrintClick,
		initContainerData,
	};
};
